import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import type { Zone, ZoneStatus } from '../../services/zonesApi';
import { CONFIG_SCOPE_LABELS } from '../../utils/configVocabulary';
import ZoneStatusBadge from './ZoneStatusBadge';

export interface ZoneTableRowModel {
  id: string;
  zone: Zone;
  status?: ZoneStatus;
  displayName: string;
  secondaryId: string;
  targetDeviceLabel: string;
  targetCameraLabel: string;
  typeLabel: string;
  snapshotLabel: string;
  policyLabel: string;
  occupancyLabel: string;
  statusLabel: string;
  lastUpdatedLabel: string;
}

export interface ZoneTableProps {
  rows: ZoneTableRowModel[];
  loading?: boolean;
  selectedZoneId?: string | null;
  onSelectRow: (row: ZoneTableRowModel) => void;
  onOpenActions: (event: React.MouseEvent<HTMLElement>, row: ZoneTableRowModel) => void;
}

const nameCell = (row: ZoneTableRowModel) => (
  <Box sx={{ minWidth: 0 }}>
    <Tooltip title={row.displayName}>
      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
        {row.displayName}
      </Typography>
    </Tooltip>
    {row.secondaryId ? (
      <Tooltip title={row.secondaryId}>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 280 }}>
          {row.secondaryId}
        </Typography>
      </Tooltip>
    ) : null}
  </Box>
);

const targetCell = (row: ZoneTableRowModel) => (
  <Box sx={{ minWidth: 0 }}>
    <Tooltip title={row.targetDeviceLabel || CONFIG_SCOPE_LABELS.noEdgeDevice}>
      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
        {row.targetDeviceLabel || CONFIG_SCOPE_LABELS.noEdgeDevice}
      </Typography>
    </Tooltip>
    <Tooltip title={row.targetCameraLabel || CONFIG_SCOPE_LABELS.noCameraStream}>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>
        {row.targetCameraLabel || CONFIG_SCOPE_LABELS.noCameraStream}
      </Typography>
    </Tooltip>
  </Box>
);

const ZoneTable: React.FC<ZoneTableProps> = ({
  rows,
  loading = false,
  selectedZoneId = null,
  onSelectRow,
  onOpenActions,
}) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));

  if (compact) {
    return (
      <Stack spacing={1.25}>
        {rows.map((row) => {
          const selected = selectedZoneId === row.id;
          return (
            <Paper
              key={row.id}
              variant="outlined"
              onClick={() => onSelectRow(row)}
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                borderColor: selected ? 'primary.main' : 'divider',
                boxShadow: selected ? '0 0 0 1px rgba(25, 118, 210, 0.15)' : 'none',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {nameCell(row)}
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                    <ZoneStatusBadge label={row.statusLabel} />
                    <ZoneStatusBadge label={row.snapshotLabel} kind="snapshot" />
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  aria-label="Zone actions"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenActions(event, row);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  mt: 1.25,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">
                    {CONFIG_SCOPE_LABELS.edgeDevice} / {CONFIG_SCOPE_LABELS.cameraStream}
                  </Typography>
                  {targetCell(row)}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body2">{row.typeLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Policies
                  </Typography>
                  <Typography variant="body2">{row.policyLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Occupancy
                  </Typography>
                  <Typography variant="body2">{row.occupancyLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last updated
                  </Typography>
                  <Typography variant="body2">{row.lastUpdatedLabel}</Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
        {!loading && rows.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No zones match the current filters.
            </Typography>
          </Paper>
        ) : null}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              '& .MuiTableCell-root': {
                bgcolor: '#faf8f4',
                borderBottom: '1px solid #e5e7eb',
                color: '#475467',
                fontWeight: 700,
              },
            }}
          >
            <TableCell>Zone</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Snapshot</TableCell>
            <TableCell>Policies</TableCell>
            <TableCell>Occupancy</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last Updated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const selected = selectedZoneId === row.id;
            return (
              <TableRow
                key={row.id}
                hover
                onClick={() => onSelectRow(row)}
                sx={{
                  cursor: 'pointer',
                  '& .MuiTableCell-root': { borderBottom: '1px solid #eef1f4' },
                  ...(selected
                    ? {
                        bgcolor: 'rgba(29, 79, 145, 0.07)',
                        boxShadow: 'inset 3px 0 0 #1d4f91',
                      }
                    : null),
                }}
              >
                <TableCell sx={{ maxWidth: 320 }}>{nameCell(row)}</TableCell>
                <TableCell>{targetCell(row)}</TableCell>
                <TableCell>{row.typeLabel}</TableCell>
                <TableCell>
                  <ZoneStatusBadge label={row.snapshotLabel} kind="snapshot" />
                </TableCell>
                <TableCell>
                  <Tooltip title={row.policyLabel}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {row.policyLabel}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{row.occupancyLabel}</TableCell>
                <TableCell>
                  <ZoneStatusBadge label={row.statusLabel} />
                </TableCell>
                <TableCell>{row.lastUpdatedLabel}</TableCell>
                <TableCell align="right">
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelectRow(row);
                      }}
                    >
                      View
                    </Button>
                    <IconButton
                      size="small"
                      aria-label="Zone actions"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenActions(event, row);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography variant="body2" color="text.secondary">
                  No zones match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ZoneTable;
